import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

// Actions
import {
  downloadDataLayerTemplate,
  uploadDataLayerTemplate,
} from "../../../actions/org/OrgDataLayerActions";

// Constants and Utils
import { selectFile } from "../../../common-utils/file-utils/FileUtils";
import { OrgSettingsIndexes } from "../../../constants/GeneralConstants";
import { FilenameExtensions } from "../../../constants/GeneralConstants";

// Components
import { PlainDropdown } from "../../../components/dropdown/Dropdown";
import OrgSettingsSidebar from "../OrgSettingsSidebar";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import DownloadTemplateCard from "../../../components/site-upload-utils/DownloadTemplateCard";
import UploadTemplateCard from "../../../components/site-upload-utils/UploadTemplateCard";

// CSS
import "../OrgSettings.scss";

// Page-Constants
const dataLayerUpload = {
  DownloadTemplateData: {
    step: "STEP 2",
    title: "Download and fill template",
    message: "If you already have a filled template jump to next step.",
    buttonLabel: "Download Template",
  },
  UploadSiteListData: {
    step: "STEP 3",
    title: "Upload data layer",
    message:
      "Please upload using the template format recommended to avoid errors.",
    buttonLabel: "Upload Template",
  },
};

// Page Components
function CustomLayerInput({ onItemSelect, onTextChange }) {
  const dropdownItems = [{ id: "POINT", label: "Points Layer" }];

  function onInputTextChange(event) {
    onTextChange(event.target.value);
  }

  return (
    <div className="d-flex">
      <PlainDropdown
        items={dropdownItems}
        onItemSelect={onItemSelect}
        placeHolder="Select type"
        className="border-secondary btn shadow-none"
      />
      <input
        className="col-5 form-control border-secondary shadow-none"
        placeholder="Enter data layer name"
        onChange={onInputTextChange}
      />
    </div>
  );
}

function LayerDetailsStep({ onItemSelect, onTextChange }) {
  return (
    <div className="card border-0 mt-3">
      <div className="card-content">
        <p className="card__step">STEP 1</p>
        <p className="card__title">Data layer details</p>
        <p className="card__message">
          This will be used in the rest of the product as a reference name.
        </p>
      </div>
      <CustomLayerInput
        onItemSelect={onItemSelect}
        onTextChange={onTextChange}
      />
    </div>
  );
}

/**
 * Page Component
 */
export default function DataLayerCreatePage() {
  const dispatch = useDispatch();
  const history = useHistory();

  // State
  const [layerType, setLayerType] = useState("");
  const [layerName, setLayerName] = useState("");

  // Selector States
  const isDownloadingTemplate = useSelector(
    (state) => state.orgDataLayers.downloadTemplateLoading
  );

  // Functions
  function onDownloadTemplateClick() {
    if (!layerType) {
      toast.error("Select a layer type");
      return;
    }

    const fileName = `Data_Layer_Template${FilenameExtensions.xlsx}`;
    dispatch(downloadDataLayerTemplate(layerType, fileName));
  }

  async function onUploadTemplateClick() {
    if (!layerType || !layerName) {
      toast.error("Select a layer type and enter data layer name");
      return;
    }

    const files = await selectFile();
    dispatch(uploadDataLayerTemplate(layerType, layerName, files, history));
  }

  // object for reducerKey and selectorValues to pass in "uploadTemplate"
  const reducerKeyValuesObj = {
    reducerKey: "orgDataLayers",
    selectorValue: {
      loading: "uploadTemplateLoading",
      error: "uploadTemplateError",
    },
  };

  return (
    <>
      <OrgSettingsSidebar activeIdx={OrgSettingsIndexes.dataLayers} />

      <div className="content-wrapper site-upload">
        {/* Page-Header */}
        <PageHeader title={"New Data Layer"} shadow={true} border={true} />

        <LayerDetailsStep
          onItemSelect={(id) => setLayerType(id)}
          onTextChange={(name) => setLayerName(name)}
        />

        {/* Data-layer Download */}
        <DownloadTemplateCard
          isDisable={isDownloadingTemplate}
          onClick={onDownloadTemplateClick}
          downloadTemplateData={dataLayerUpload.DownloadTemplateData}
        />

        {/* Upload Data-layer */}
        <UploadTemplateCard
          uploadSiteListData={dataLayerUpload.UploadSiteListData}
          reducerKeyValuesObj={reducerKeyValuesObj}
          uploadTemplateFunction={onUploadTemplateClick}
          isImageUploadRequired={false}
        />
      </div>
    </>
  );
}
